// src/components/Users/Profile.js
import React, { useEffect, useRef, useState } from "react";
import { Tabs, Tab, Box } from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { Link, useNavigate } from "react-router-dom";
import "./Profile.css";
import GuestsStats from "../Organizer/Guests/GuestsStats";
import SponsoredSlot from "../Sponsored/SponsoredSlot";
import BACKOFFICE_BASE_API_V1 from "../../utils/backofficeApi";
import { getMyFavorites } from "../../utils/favoritesApi";
const defaultAvatarImage = `${process.env.PUBLIC_URL || ""}/assets/kazhavda_profile_pic.jpg`;

const venueCategories = [
  { id: "all", name: "Сите категории" },
  { id: "332c6a1c-fc3d-4a04-90a1-326014f81bc1", name: "Облека" },
  { id: "c9163180-8567-4c5a-b76a-6abc0b9f73ec", name: "Музика" },
  { id: "fcbca939-d505-489f-9b5c-630ea1f3e430", name: "Декорации" },
  { id: "647d5787-4fb6-499a-b5a1-4356b438c89a", name: "Фотографи" },
  { id: "0b7588f1-3e5c-47a5-9e7e-5e23f7cbf6a8", name: "Козметика" },
  { id: "d3f092e6-b1fa-4c89-b6ad-82cde08f9be1", name: "Ресторани" },
  { id: "15a858ea-2d38-4deb-9578-4139d3db6187", name: "Кетеринг/Торти" },
  { id: "5b52bd52-11ea-4b54-a964-6539368b4989", name: "Хотели" },
  { id: "2cbadf3a-6f10-40d8-b2bc-e45c838c6154", name: "Останато" },
];

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  const adjusted = new Date(date.getTime() - offset * 60 * 1000);
  return adjusted.toISOString().slice(0, 16);
};

const formatWeddingDate = (value) => {
  if (!value) return "Нема внесен датум";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "Нема внесен датум";
  }
  return parsed.toLocaleString("mk-MK", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function Profile() {
  const { authPayload, logout } = useAuth();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_BASE;

  const [userRecord, setUserRecord] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [formData, setFormData] = useState({
    groom_name: "",
    groom_last_name: "",
    bride_name: "",
    bride_last_name: "",
    phone_number: "",
  });

  const [statusMessage, setStatusMessage] = useState({ text: "", type: "" });
  const [avatarMessage, setAvatarMessage] = useState({ text: "", type: "" });
  const [avatarPreview, setAvatarPreview] = useState(defaultAvatarImage);
  const [avatarFile, setAvatarFile] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [weddingForm, setWeddingForm] = useState({
    date: "",
  });
  const [weddingMessage, setWeddingMessage] = useState({ text: "", type: "" });
  const [loadingWedding, setLoadingWedding] = useState(false);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoritesError, setFavoritesError] = useState("");
  const [favoriteVenues, setFavoriteVenues] = useState([]);
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [venueCategoryFilter, setVenueCategoryFilter] = useState("all");
  const [productPriceSort, setProductPriceSort] = useState("none");

  useEffect(() => {
    async function fetchProfile() {
      try {
        const response = await fetchWithAuth(`${API}/users/{user_id}`, {
          method: "GET",
        });
        if (!response.ok) throw new Error("Failed to load profile");

        const user = await response.json();
        setUserRecord(user);
        setFormData({
          groom_name: user.groom_name || "",
          groom_last_name: user.groom_last_name || "",
          bride_name: user.bride_name || "",
          bride_last_name: user.bride_last_name || "",
          phone_number: user.phone_number || "",
        });
      } catch (error) {
        console.error("Error loading profile:", error);
        logout();
        navigate("/login");
      } finally {
       setLoadingProfile(false);
      }
    }

    if (authPayload?.id_token) {
      fetchProfile();
    } else {
      navigate("/login");
    }
  }, [authPayload, API, logout, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setStatusMessage({ text: "", type: "" });
    try {
      const response = await fetchWithAuth(`${API}/users`, {
        method: "PUT",
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Неуспешно ажурирање на профилот.");
      }

      setStatusMessage({
        text: "Профилот е успешно ажуриран.",
        type: "success",
      });

      setUserRecord((prev) => ({ ...prev, ...formData }));
    } catch (error) {
      console.error("Update error:", error);
      setStatusMessage({
        text: error.message || "Грешка при ажурирање.",
        type: "error",
      });
    }
  };

  useEffect(() => {
    if (userRecord?.profile_picture) {
      setAvatarPreview(userRecord.profile_picture);
    } else {
      setAvatarPreview(defaultAvatarImage);
    }
  }, [userRecord]);

  const handleSelectAvatar = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setAvatarMessage({ text: "", type: "" });
    setAvatarFile(file);

    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);
  };

  useEffect(() => {
    return () => {
      if (avatarPreview && avatarPreview.startsWith("blob:")) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  const handleAvatarUpload = async () => {
    if (!avatarFile) {
      setAvatarMessage({
        text: "Изберете фотографија за прикачување.",
        type: "error",
      });
      return;
    }

    const formDataPayload = new FormData();
    formDataPayload.append("file", avatarFile);

    try {
      setUploadingAvatar(true);
      setAvatarMessage({ text: "", type: "" });

      const response = await fetchWithAuth(`${API}/profile-picture`, {
        method: "POST",
        body: formDataPayload,
      });

      if (!response || !response.ok) {
        throw new Error("Неуспешно прикачување на фотографијата.");
      }

      let updatedUrl =
        userRecord?.profile_picture || avatarPreview || defaultAvatarImage;
      try {
        const responseData = await response.json();
        updatedUrl =
          responseData.profile_picture ||
          responseData.url ||
          responseData.profile_picture_url ||
          avatarPreview;
      } catch (parseError) {
        console.warn("Could not parse avatar upload response:", parseError);
      }

      setUserRecord((prev) =>
        prev ? { ...prev, profile_picture: updatedUrl } : prev
      );
      setAvatarMessage({
        text: "Фотографијата е успешно прикачена.",
        type: "success",
      });
      setAvatarFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setAvatarPreview(updatedUrl || defaultAvatarImage);
    } catch (error) {
      console.error("Avatar upload error:", error);
      setAvatarMessage({
        text:
          error.message ||
          "Настана проблем при прикачување на фотографијата.",
        type: "error",
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (
      window.confirm("Дали сте сигурни дека сакате да го избришете профилот? Ова не може да се врати.")
    ) {
      try {
        const response = await fetchWithAuth(`${API}/users/${userRecord.id}`, {
          method: "DELETE",
        });

        if (!response.ok) throw new Error("Неуспешно бришење на профилот.");
        setStatusMessage({ text: "Профилот е успешно избришан.", type: "success" });
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 1500);
      } catch (error) {
        console.error("Error:", error);
        setStatusMessage({
          text: "Грешка при бришење на профилот.",
          type: "error",
        });
      }
    }
  };

  useEffect(() => {
    if (!authPayload?.id_token) {
      return;
    }
    const loadWedding = async () => {
      setLoadingWedding(true);
      setWeddingMessage({ text: "", type: "" });
      try {
        const response = await fetchWithAuth(`${API}/weddings/my`, {
          method: "GET",
        });
        if (!response.ok) {
          throw new Error("Не може да се вчита датумот за свадбата.");
        }
        const data = await response.json();
        setWeddingForm({
          date: data?.date ? toDateTimeLocal(data.date) : "",
        });
      } catch (error) {
        console.error("Wedding fetch error:", error);
        setWeddingMessage({
          text: error.message || "Настана грешка при вчитување на датумот.",
          type: "error",
        });
      } finally {
        setLoadingWedding(false);
      }
    };
    loadWedding();
  }, [API, authPayload]);

  const handleWeddingChange = (event) => {
    const { name, value } = event.target;
    setWeddingForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleWeddingSave = async () => {
    setWeddingMessage({ text: "", type: "" });
    try {
      const payload = {
        date: weddingForm.date ? new Date(weddingForm.date).toISOString() : null,
        confirmed: false,
      };
      const response = await fetchWithAuth(`${API}/weddings/my`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Неуспешно зачувување на датумот.");
      }
      setWeddingMessage({
        text: "Деталите за свадбата се зачувани.",
        type: "success",
      });
    } catch (error) {
      console.error("Wedding save error:", error);
      setWeddingMessage({
        text: error.message || "Грешка при зачувување.",
        type: "error",
      });
    }
  };

  useEffect(() => {
    if (!authPayload?.id_token || activeTab !== 2) {
      return;
    }

    let isMounted = true;

    const fetchFavorites = async () => {
      try {
        setFavoritesLoading(true);
        setFavoritesError("");

        const payload = await getMyFavorites();
        const venueIds = Array.isArray(payload?.venue_ids)
          ? payload.venue_ids
          : [];
        const productIds = Array.isArray(payload?.product_ids)
          ? payload.product_ids
          : [];

        const venueRequests = venueIds.map((venueId) =>
          fetch(
            `${BACKOFFICE_BASE_API_V1}/venues/${encodeURIComponent(
              String(venueId)
            )}`
          )
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => data?.venue || null)
            .catch(() => null)
        );

        const productRequests = productIds.map((productId) =>
          fetch(
            `${BACKOFFICE_BASE_API_V1}/products/${encodeURIComponent(
              String(productId)
            )}`
          )
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => data?.product || data || null)
            .catch(() => null)
        );

        const [venuesResult, productsResult] = await Promise.all([
          Promise.all(venueRequests),
          Promise.all(productRequests),
        ]);

        if (!isMounted) {
          return;
        }

        setFavoriteVenues(venuesResult.filter(Boolean));
        setFavoriteProducts(productsResult.filter(Boolean));
      } catch (error) {
        console.error("Error fetching favorites:", error);
        if (isMounted) {
          setFavoritesError("Неуспешно вчитување на омилени.");
          setFavoriteVenues([]);
          setFavoriteProducts([]);
        }
      } finally {
        if (isMounted) {
          setFavoritesLoading(false);
        }
      }
    };

    fetchFavorites();

    return () => {
      isMounted = false;
    };
  }, [activeTab, authPayload?.id_token]);

  const displayedFavoriteVenues = favoriteVenues.filter((venue) => {
    if (venueCategoryFilter === "all") {
      return true;
    }
    return String(venue?.category_id || "") === venueCategoryFilter;
  });

  const displayedFavoriteProducts = [...favoriteProducts].sort((a, b) => {
    if (productPriceSort === "none") {
      return 0;
    }
    const priceA = Number(a?.price ?? 0);
    const priceB = Number(b?.price ?? 0);
    if (productPriceSort === "asc") {
      return priceA - priceB;
    }
    return priceB - priceA;
  });

  if (loadingProfile) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }
    if (!userRecord) return <div>Не може да се најде профил.</div>;

  const labels = {
    groom_name: "Име на младоженец:",
    groom_last_name: "Презиме на младоженец:",
    bride_name: "Име на невеста:",
    bride_last_name: "Презиме на невеста:",
    phone_number: "Телефонски број:",
  };

  return (
    <>
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        aria-label="profile tabs"
        TabIndicatorProps={{ style: { backgroundColor: 'var(--primary-color)' } }}
        sx={{
          '.MuiTabs-flexContainer': { gap: 2 },
          '.MuiTab-root': {
            textTransform: 'none',
            color: '#7a7a7a',
            fontWeight: 500,
            '&.Mui-selected': {
              color: 'var(--primary-color)',
              fontWeight: 700,
            },
          },
        }}
      >
        <Tab label="Детали" />
        <Tab label="Статистика" />
        <Tab label="Омилени" />
      </Tabs>
    </Box>

    {activeTab === 0 && (
    <div className="profile-container">
      <div className="profile-avatar-container">
        <img
          src={avatarPreview || defaultAvatarImage}
          alt="User Avatar"
          className="profile-avatar"
          onError={(event) => {
            const imgEl = event.currentTarget;
            if (imgEl.src !== defaultAvatarImage) {
              imgEl.src = defaultAvatarImage;
            }
          }}
        />
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleAvatarChange}
          className="profile-avatar-input"
        />
        <div className="profile-avatar-actions">
          <button
            type="button"
            className="profile-button choose-avatar-button"
            onClick={handleSelectAvatar}
          >
            Избери фотографија
          </button>
          <button
            type="button"
            className="profile-button upload-avatar-button"
            onClick={handleAvatarUpload}
            disabled={uploadingAvatar || !avatarFile}
          >
            {uploadingAvatar ? "Се прикачува..." : "Прикачи фотографија"}
          </button>
        </div>
        {avatarMessage.text && (
          <div
            className={`status-message avatar-message ${
              avatarMessage.type === "success" ? "success" : "error"
            }`}
          >
            {avatarMessage.text}
          </div>
        )}
      </div>

      <div className="profile-card">
        <h2 className="profile-header">Добре дојде, {userRecord.email}</h2>

        <div className="wedding-highlight-card">
          <div className="wedding-highlight-header">
            <p className="wedding-highlight-eyebrow">Главен датум</p>
            <h3>Датум на свадба</h3>
            <p className="wedding-highlight-subtitle">
              Овој датум автоматски се користи за поканата и Save the Date.
            </p>
          </div>
          <p className={`wedding-date-display ${weddingForm.date ? "filled" : "empty"}`}>
            {formatWeddingDate(weddingForm.date)}
          </p>
          <div className="wedding-date-controls">
            <div className="editable-field wedding-date-field">
              <label className="editable-label">Одбери датум и време</label>
              <div className="editable-input-wrapper">
                <input
                  type="datetime-local"
                  name="date"
                  value={weddingForm.date}
                  onChange={handleWeddingChange}
                  className="editable-input-field wedding-date-input"
                  disabled={loadingWedding}
                />
              </div>
            </div>
            <button
              className="profile-button wedding-save-button"
              onClick={handleWeddingSave}
              disabled={loadingWedding}
            >
              {loadingWedding ? "Се зачувува..." : "Зачувај датум"}
            </button>
          </div>
          {weddingMessage.text && (
            <div
              className={`status-message ${
                weddingMessage.type === "success" ? "success" : "error"
              }`}
            >
              {weddingMessage.text}
            </div>
          )}
        </div>

        <div className="profile-form-grid">
          {Object.keys(formData).map((field) => (
            <div key={field} className="editable-field">
              <label className="editable-label">{labels[field]}</label>
              <div className="editable-input-wrapper">
                <input
                  type="text"
                  name={field}
                  value={formData[field]}
                  onChange={handleChange}
                  spellCheck={false}
                  className="editable-input-field"
                />
              </div>
            </div>
          ))}
        </div>

        <div className="profile-action-container">
          <div className="profile-button-container">
            <button className="profile-button edit-profile-button" onClick={handleSave}>
              Зачувај промени
            </button>
            <button className="profile-button delete-account-button" onClick={handleDeleteAccount}>
              Избриши го профилот
            </button>
          </div>
          {statusMessage.text && (
            <div className={`status-message ${statusMessage.type === "success" ? "success" : "error"}`}>
              {statusMessage.text}
            </div>
          )}
        </div>
      </div>
    </div>
    )}

    {activeTab === 1 && (
      <div className="profile-stats-section">
        <h3 className="profile-stats-title">Статистика за гости</h3>
        <GuestsStats />
      </div>
    )}

    {activeTab === 2 && (
      <div className="profile-favorites-section">
        <h3 className="profile-favorites-title">Омилени</h3>

        <div className="favorites-filters">
          <div className="favorites-filter-group">
            <label htmlFor="favorites-venue-category">Категорија на локации</label>
            <select
              id="favorites-venue-category"
              value={venueCategoryFilter}
              onChange={(event) => setVenueCategoryFilter(event.target.value)}
            >
              {venueCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="favorites-filter-group">
            <label htmlFor="favorites-product-sort">Сортирање на продукти</label>
            <select
              id="favorites-product-sort"
              value={productPriceSort}
              onChange={(event) => setProductPriceSort(event.target.value)}
            >
              <option value="none">Без сортирање</option>
              <option value="asc">Цена - растечка</option>
              <option value="desc">Цена - опаѓачка</option>
            </select>
          </div>
        </div>

        {favoritesLoading ? (
          <p className="favorites-status-message">Се вчитуваат омилените...</p>
        ) : favoritesError ? (
          <p className="favorites-status-message error">{favoritesError}</p>
        ) : (
          <>
            <section className="favorites-block">
              <h4>Локации</h4>
              {displayedFavoriteVenues.length > 0 ? (
                <div className="favorites-grid">
                  {displayedFavoriteVenues.map((venueItem) => (
                    <Link
                      key={venueItem.id}
                      to={`/venue/${venueItem.id}`}
                      className="favorite-card-link"
                    >
                      <article className="favorite-card favorite-venue-card">
                        <img
                          src={
                            venueItem.image_url ||
                            "https://placehold.co/600x400/weddings/white"
                          }
                          alt={venueItem.name}
                        />
                        <div className="favorite-card-content">
                          <span className="favorite-card-eyebrow">VENUE</span>
                          <h5>{venueItem.name || "Непозната локација"}</h5>
                          <p>{venueItem.city || "Непознат град"}</p>
                          {venueItem.phone_number ? (
                            <p className="favorite-card-secondary">
                              {venueItem.phone_number}
                            </p>
                          ) : null}
                        </div>
                      </article>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="favorites-empty">
                  Нема омилени локации за избраната категорија.
                </p>
              )}
            </section>

            <section className="favorites-block">
              <h4>Продукти</h4>
              {displayedFavoriteProducts.length > 0 ? (
                <div className="favorites-grid">
                  {displayedFavoriteProducts.map((productItem) => (
                    <Link
                      key={productItem.id}
                      to={`/product/${productItem.id}?viewed_from=standard`}
                      className="favorite-card-link"
                    >
                      <article className="favorite-card favorite-product-card">
                        <img
                          src={
                            productItem.images?.[0] ||
                            "https://placehold.co/600x400/weddings/white"
                          }
                          alt={productItem.name}
                        />
                        <div className="favorite-card-content">
                          <span className="favorite-card-eyebrow">ПРОДУКТ</span>
                          <h5>{productItem.name || "Непознат продукт"}</h5>
                          <p>{productItem.venue_name || "Непозната локација"}</p>
                          <p className="favorite-card-price">
                            {productItem.price ? `${productItem.price},00 ден` : "—"}
                          </p>
                        </div>
                      </article>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="favorites-empty">Нема омилени продукти.</p>
              )}
            </section>
          </>
        )}
      </div>
    )}
    <SponsoredSlot placement="profile.bottom" />
    </>
  );
}

export default Profile;
